/**
 * Answers the native shell's "can you go back?" question.
 *
 * The Android hardware back button is delivered to React Native JS, which has
 * to decide synchronously whether to swallow the press — but only the web app
 * knows whether a modal is open or a panel is behind us. So the native side
 * always swallows the press, asks us here, and backgrounds the app only if we
 * answer 'exhausted' (or never answer at all, which is why it also runs a
 * watchdog: a web bundle without this listener must not trap the user).
 *
 * Mirrors the shape of the native keyboard bridge — native injects a
 * CustomEvent, we reply over ReactNativeWebView.postMessage.
 */

import { postMessageToNative } from 'utils/nativeBridge'
import type { BackNavigationResult } from 'state/backNavigationPolicy'

export const NATIVE_BACK_REQUEST_EVENT = 'nativeBackRequest'
export const NATIVE_BACK_RESULT_KIND = 'nativeBackResult'

export type NativeBackRequest = {
    requestId: string
}

/**
 * Native controls the requestId, so validate it rather than trusting the
 * injected payload's shape.
 */
export function parseNativeBackRequest(detail: unknown): NativeBackRequest | null {
    if (!detail || typeof detail !== 'object') return null
    const requestId = (detail as { requestId?: unknown }).requestId
    if (typeof requestId !== 'string' || !requestId) return null
    return { requestId }
}

export function buildNativeBackResult(requestId: string, result: BackNavigationResult): string {
    return JSON.stringify({ kind: NATIVE_BACK_RESULT_KIND, requestId, result })
}

type NativeBackBridgeOptions = {
    /** Required, so this module never pulls the store into a caller's bundle. */
    navigate: () => BackNavigationResult
    target?: Pick<Window, 'addEventListener' | 'removeEventListener'>
    postMessage?: (message: string) => boolean
}

/**
 * Starts answering native back requests. Returns a cleanup function.
 */
export function initNativeBackBridge({
    navigate,
    target = typeof window === 'undefined' ? undefined : window,
    postMessage = message => postMessageToNative(message),
}: NativeBackBridgeOptions): () => void {
    if (!target) return () => {}

    const handleRequest = (event: Event) => {
        const request = parseNativeBackRequest((event as CustomEvent).detail)
        if (!request) return

        let result: BackNavigationResult = 'exhausted'
        try {
            result = navigate()
        } catch (error) {
            // Never leave the press unanswered: an exception here would strand
            // the user until the native watchdog fires.
            console.error('Back navigation failed', error)
        }
        postMessage(buildNativeBackResult(request.requestId, result))
    }

    target.addEventListener(NATIVE_BACK_REQUEST_EVENT, handleRequest)
    return () => target.removeEventListener(NATIVE_BACK_REQUEST_EVENT, handleRequest)
}
